import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { createNewResource } from '../../services/content.service';
import AssetUpload from './asset-upload.component';
import ResourceType from '../form-elements/resource-type.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddResource = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: null,
      type: null,
      description: null,
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [resourceData, setResourceData] = useState(null);

  const resourceDataCallback = (data) => {
    setResourceData(data);
  };

  const saveNewData = (payload) => {
    createNewResource(payload)
      .then(() => {
        onSuccess('New resource added successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (!resourceData || !resourceData?.resourceUrl) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'info',
          message: 'Please upload resource before save data.',
        });
      }, 100);
      setFormSubmitted(false);
      return;
    }

    const payload = {
      name: data.name || null,
      type: data.type || null,
      description: data.description || null,
      mimeType: resourceData.mimeType || null,
      filename: resourceData.filename || null,
      resourceUrl: resourceData.resourceUrl || null,
      fileSize: resourceData.fileSize || 0,
    };
    if (!dataId) {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
    }
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(255),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="type"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <ResourceType
                      id="resource_type"
                      name="resource_type"
                      label="Type"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[1, 0]}
                      sx={{ width: '100%' }}
                      error={errors?.type?.message || ''}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    ...maxLengthValidation(1000),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      type="text"
                      multiline
                      minRows={3}
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Box
                  component="section"
                  sx={{
                    p: 3,
                    border: '2px solid lightgrey',
                    borderRadius: '5px',
                    width: '100%',
                  }}
                >
                  <AssetUpload
                    title="Resource"
                    resourceUrl={resourceData?.resourceUrl || ''}
                    resourceDataCallback={resourceDataCallback}
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ marginRight: 2 }}>
        <Button
          color="inherit"
          variant="outlined"
          onClick={onCancel}
          disabled={formSubmitted}
          sx={{ borderRadius: '10px' }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
          sx={{
            borderRadius: '10px',
            boxShadow: (theme) => theme.customShadows.primary,
          }}
        >
          {dataId ? 'Update Details' : 'Create Now'}
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddResource.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddResource;
